import React from "react";
import { useNavigate } from "react-router-dom";
import { Col, Row, Spinner } from "react-bootstrap";
import { useQuery } from "@apollo/client";
import { KPI_METRICS_QUERY } from "api/graphql/queries/kpiMetricsQuery";
import EcomTotalOrdersCard from "components/cards/EcomTotalOrdersCard";
import EcomProductQtySoldCard from "components/cards/EcomProductQtySoldCard";
import TransactionSalesSummary from "components/cards/TransactionSalesSummary";
import TransactionMetricsDetail from "components/cards/TransactionMetricsDetail";
import EcomBestSellingProductsTable from "components/tables/EcomBestSellingProductsTable";
import EcomTopRegionsMap from "components/leaflet-maps/EcomTopRegionsMap";
import EnhancedEcomStats from "components/stats/EnhancedEcomStats";
import { mapMarkerPoints } from "data/mapMarkerPoints";
import EcomProjectionVsActual from "components/modules/e-commerce/dashboard/EcomProjectionVsActual";
import EcomReturningCustomerRate from "components/modules/e-commerce/dashboard/EcomReturningCustomerRate";
import EcomTotalSells from "components/modules/e-commerce/dashboard/EcomTotalSells";
import EcomTopRegions from "components/modules/e-commerce/dashboard/EcomTopRegions";
import { useTranslation } from "react-i18next"; // Import the hook

const Ecommerce = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const { data, loading, error } = useQuery(KPI_METRICS_QUERY, {
    fetchPolicy: 'network-only', // This ensures we always get fresh data from the server
    nextFetchPolicy: 'cache-first' // After the first fetch, we can use cache
  });
  
  // Extract KPI data from the API response
  const kpiData = {
    // Order stats
    newOrders: data?.kpi?.newOrders || 0,
    completeOrders: data?.kpi?.completeOrders || 0,
    readyToReturnOrders: data?.kpi?.readyToReturnOrders || 0,
    
    // Product stats
    totalProducts: data?.kpi?.totalProducts || 0,
    lowStockProducts: data?.kpi?.lowStockProducts || 0,
    closedOrDisapprovedProducts: data?.kpi?.closedOrDisapprovedProducts || 0
  };
  return (
    <>
      <div
        className="pb-5"
        style={{
          textAlign: isArabic ? "right" : "left", // Apply text alignment based on language
          direction: isArabic ? "rtl" : "ltr", // Apply direction change for Arabic
        }}
      >
        <div className="mb-8">
              <h2 className="mb-2">{t("ecommerceDashboardTitle")}</h2>
              <h5 className="text-body-tertiary fw-semibold">
                {t("ecommerceDashboardSubtitle")}
              </h5>
            </div>
        <Row className="g-4">
          <Col xs={12} xxl={6}>
            
            {loading ? (
              <div className="text-center my-5">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">{t("loading")}</span>
                </Spinner>
              </div>
            ) : (
              <EnhancedEcomStats 
                // Order stats
                newOrders={kpiData.newOrders}
                completeOrders={kpiData.completeOrders}
                readyToReturnOrders={kpiData.readyToReturnOrders}
                
                // Product stats
                totalProducts={kpiData.totalProducts}
                lowStockProducts={kpiData.lowStockProducts}
                closedOrDisapprovedProducts={kpiData.closedOrDisapprovedProducts}
                
                loading={loading}
                
                // Click handlers for orders
                onNewOrdersClick={() => navigate('/orders', {
                  state: { 
                    page: 1, 
                    pageSize: 10,
                    filter: { order_status: ['processing'] }
                  }
                })}
                onPendingPaymentOrdersClick={() => navigate('/orders', {
                  state: { 
                    page: 1, 
                    pageSize: 10,
                    filter: { order_status: ['complete'] }
                  }
                })}
                onPaidOrdersClick={() => navigate('/orders', {
                  state: { 
                    page: 1, 
                    pageSize: 10,
                    filter: { order_status: ['Ready_to_return'] }
                  }
                })}
                
                // Click handlers for products
                onTotalProductsClick={() => navigate('/products', {
                  state: { 
                    page: 1, 
                    pageSize: 10
                  }
                })}
                onLowStockProductsClick={() => navigate('/products', {
                  state: { 
                    page: 1, 
                    pageSize: 10,
                    filter: { 
                      qty_from: 0, 
                      qty_to: 5,
                      type_id: 'simple'
                    }
                  }
                })}
                onClosedOrDisapprovedProductsClick={() => navigate('/products', {
                  state: { 
                    page: 1, 
                    pageSize: 10,
                    filter: { product_status: 2 }
                  }
                })}
              />
            )}
            <hr className="bg-body-secondary mb-6 mt-4" />
            <EcomTotalSells />
          </Col>
          <Col xs={12} xxl={6}>
            <Row className="g-3 mb-4">
          <Col xs={12} md={6}>
            <EcomTotalOrdersCard 
              orderStats={data?.kpi?.orderStats}
              totalOrders={data?.kpi?.totalOrders}
              loading={loading}
            />
          </Col>
              <Col xs={12} md={6}>
                <EcomProductQtySoldCard />
              </Col>
              <Col xs={12} md={6}>
                <TransactionSalesSummary />
              </Col>
              <Col xs={12} md={6}>
                <TransactionMetricsDetail />
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
      <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis pt-7 border-y">
        <EcomBestSellingProductsTable />
      </div>
      {/* <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis pt-6 pb-9 border-top">
        <Row className="g-6">
          <Col xs={12} xl={6}>
            <EcomProjectionVsActual />
          </Col>
          <Col xs={12} xl={6}>
            <EcomReturningCustomerRate />
          </Col>
        </Row>
      </div> */}
    </>
  );
};

export default Ecommerce;
